import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";

import type { LedgerLabels } from "../hooks/useLedgerLabels";
import type { LedgerAccount } from "../types/paymentsLedger.types";

export interface LedgerAccountCellProps {
  account: LedgerAccount;
  labels: LedgerLabels;
}

/**
 * How an account is presented everywhere on this surface: its translated name, the accounting code it maps
 * to, its type, and who it belongs to.
 *
 * Two deliberate behaviours. A code outside the mirrored catalog still renders — with the backend's own name
 * and an "uncatalogued" badge — because hiding it would hide money. And a provider-scoped account is
 * described as such and no further: `scope_provider_code` never reaches the screen (rule 12).
 */
export function LedgerAccountCell({ account, labels }: LedgerAccountCellProps) {
  const { t } = useTranslations("paymentsLedger");
  const hint = labels.accountHint(account);
  const isKnown = labels.isKnownAccount(account);

  const name = <span className="text-sm font-medium text-foreground">{labels.account(account)}</span>;

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5">
        {hint ? (
          <ColocaTooltip
            triggerRender={
              <span className="cursor-help underline decoration-dotted underline-offset-2">{name}</span>
            }
          >
            {hint}
          </ColocaTooltip>
        ) : (
          name
        )}
        <ColocaBadge variant="secondary" className="font-sans">
          {labels.accountType(account)}
        </ColocaBadge>
        {!isKnown && (
          <ColocaBadge variant="warning" className="border-dashed font-sans">
            {t("account_unknown_badge")}
          </ColocaBadge>
        )}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
        <span className="font-mono">{account.code}</span>
        <span aria-hidden="true">·</span>
        <span className="truncate">{labels.accountScope(account)}</span>
      </div>
    </div>
  );
}
